<template>
    <div>
        <div class="box box-warning">
            <div class="box-header with-border">
                <h3 class="box-title">Seznam virov kontakta</h3>

                <div class="box-tools">
                    <button 
                        type="button"
                        class="btn btn-warning btn-sm pull-left"
                        title="Dodaj novo verzijo kontakta"
                        @click.prevent="showAddVersionModal">
                        <i 
                            class="fa fa-plus" 
                            aria-hidden="true"/>
                    </button>
                </div>
            </div>
            <div class="box-body table-responsive no-padding">
                <vuetable 
                    ref="vuetable"
                    :fields="fields"
                    :css="css.table"
                    :row-class="rowClass"
                    :initial-page="initialPage"
                    :per-page="perPage"
                    :sort-order="sortOrder"
                    :multi-sort="true"
                    :append-params="moreParams"
                    api-url="/ajax/contact_versions"
                    pagination-path="meta"
                    detail-row-component="contact-version-details"
                    @vuetable:cell-clicked="onCellClicked"
                    @vuetable:pagination-data="onPaginationData"/>
            </div>
            <div class="box-footer clearfix">
                <div class="vuetable-pagination pagination pagination-sm no-margin pull-right">
                    <vuetable-pagination-info
                        ref="paginationInfo"
                        info-template="Prikazujem {from} do {to} od {total} virov"
                        info-class="pagination-info"/>
                    <vuetable-pagination
                        ref="pagination"
                        :css="css.pagination"
                        @vuetable-pagination:change-page="onChangePage"/>
                </div>
            </div>
        </div>
        <new-contact-version 
            :api_route="api_route"
            :contact_id="rowData.id"/>
    </div>
</template>

<script>
import Vuetable                   from 'vuetable-2/src/components/Vuetable';
import VuetablePagination         from 'vuetable-2/src/components/VuetablePagination';
import VuetablePaginationInfo     from 'vuetable-2/src/components/VuetablePaginationInfo';
import Table                      from '../../../mixins/Table';
import ContactVersionDeleteAction from '../Actions/ContactVersionDelete';
import BestVersionAction          from '../Actions/BestVersion';
import ContactsCommon             from '../../../mixins/Contacts';
import ContactVersionDetails      from './ContactVersionDetails';
import SourceLabel                from './SourceLabel';
import NewContactVersion          from '../New/NewContactVersion';

Vue.component('contact-version-details', ContactVersionDetails);
Vue.component('contact-version-delete-action', ContactVersionDeleteAction);
Vue.component('best-version-action', BestVersionAction);
Vue.component('contact-source-label', SourceLabel);
Vue.component('new-contact-version', NewContactVersion);

export default {
    name: 'ContactVersions',

    components: {
        Vuetable,
        VuetablePagination,
        VuetablePaginationInfo
    },

    mixins: [
        Table,
        ContactsCommon
    ],

    props: {
        rowData:  {
            type:     Object,
            required: true
        },
        rowIndex: {
            type:   Number,
            default: 0
        }
    },

    data() {
        return {
            api_route:                '/ajax/contact_versions',
            fields:                   [
                {
                    name:      'id',
                    title:     '#',
                    sortField: 'id',
                },
                {
                    name:      '__component:contact-source-label',
                    title:     'vir',
                    sortField: 'source'
                },
                {
                    name:       '__component:best-version-action',
                    title:      'priljubljena',
                    titleClass: 'text-center',
                    dataClass:  'text-center status category-status',
                    sortField:  'best'
                },
                {
                    name:      'edited_manually',
                    title:     'ročno urejen',
                    callback:  value => value ? '<span class="badge bg-green">da</span>' : '<span class="badge bg-default">ne</span>',
                    sortField: 'edited_manually'
                },
                {
                    name:      'email',
                    title:     'e-pošta',
                    callback:  value => value ? value : 'n/a',
                    sortField: 'email'
                },
                {
                    name:      'phone',
                    title:     'telefon',
                    callback:  value => value ? value : 'n/a',
                    sortField: 'phone'
                },
                {
                    name:      'mobile_phone',
                    title:     'GSM',
                    callback:  value => value ? value : 'n/a',
                    sortField: 'mobile_phone'
                },
                {
                    name:      'tax_id',
                    title:     'davčna',
                    callback:  value => value ? value : 'n/a',
                    sortField: 'tax_id'
                },
                {
                    name:      'name',
                    title:     'ime',
                    callback:  value => value ? value : 'n/a',
                    sortField: 'name'
                },
                {
                    name:      'sex',
                    title:     'spol',
                    callback:  value => {
                        switch (value) {
                        case 'male':
                            return '<span class="label label-primary">moški</span>';
                        case 'female':
                            return '<span class="label label-danger">ženska</span>';
                        case 'neutral':
                        default:
                            return '<span class="label label-warning">nedoločen</span>';
                        }
                    },
                    sortField: 'sex'
                },
                {
                    name:      'birthday',
                    title:     'roj.',
                    callback:  value => this.$options.filters.formatDate(value, 'YYYY'),
                    sortField: 'birthday'
                },
                {
                    name:      'address',
                    title:     'naslov',
                    callback:  value => value ? value : 'n/a',
                    sortField: 'address'
                },
                {
                    name:      'post',
                    title:     'pošta',
                    callback:  value => value ? `${value.id} ${value.name}` : 'n/a',
                    sortField: 'post'
                },
                {
                    name:      'municipality',
                    title:     'občina',
                    callback:  value => value ? value.name : 'n/a',
                    sortField: 'municipality'
                },
                {
                    name:      'region',
                    title:     'regija',
                    callback:  value => value ? value.name : 'n/a',
                    sortField: 'region'
                },
                {
                    name:      'country',
                    title:     'država',
                    callback:  value => value ? value.name : 'n/a',
                    sortField: 'country'
                },
                {
                    name:     'created_at',
                    title:    'ustvarjen',
                    callback: value => this.$options.filters.formatDate(value.date, 'DD. MM. YYYY'),
                },
                {
                    name:     'updated_at',
                    title:    'uvožen',
                    callback: value => this.$options.filters.formatDate(value.date, 'DD. MM. YYYY'),
                },
                {
                    name:       '__component:contact-version-delete-action',
                    title:      'izbriši',
                    titleClass: 'text-center',
                    dataClass:  'text-center status category-status'
                }
            ],
            perPage:                  12,
            moreParams:               {contact_id: this.rowData.id},
            css:                      {
                table: {
                    tableClass: 'table table-striped table-hover'
                }
            },
            paginationWithHistoryUrl: false
        };
    },

    watch: {
        'rowData.mailing_lists'(current) {
            this.rowData.mailing_lists_count = current.length;
        }
    },

    events: Object.assign({
        markAsBestVersion(data, forceReload = false) {
            axios.put(`${this.api_route}/${data.id}/best`)
                .then(response => {
                    data.best = true;

                    this.$toast.success({
                        title:   'USPEH!',
                        message: response.data.status
                    });

                    if (forceReload) {
                        this.$events.fire('refreshTableData');

                        Vue.nextTick(() => this.$refs.vuetable.refresh());
                    }
                })
                .catch(error => {
                    this.$toast.error({
                        title:   'NAPAKA!',
                        message: error,
                        timeOut: 10000
                    });
                });
        },

        deleteContactVersion(data, forceReload = false) {
            axios.delete(`${this.api_route}/${data.id}`)
                .then(response => {
                    data.status = false;

                    this.$toast.success({
                        title:   'USPEH!',
                        message: response.data.status
                    });

                    if (forceReload) {
                        Vue.nextTick(() => {
                            this.$refs.vuetable.refresh();
                            this.$parent.refresh();
                        });
                    }
                })
                .catch(error => {
                    this.$toast.error({
                        title:   'NAPAKA!',
                        message: error,
                        timeOut: 10000
                    });
                });
        }
    }, Table.events),

    methods: {
        showAddVersionModal() {
            if (this.$root.showModal || this.$root.showNestedModal)
                return;

            this.$set(this.$root, 'showNestedModal', true);
        }
    }
};
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/Contacts/Components/ContactVersions.vue